import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { DateTime } from 'luxon';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { Setting, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.ABMELDUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Abmeldebestätigung',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Sie soeben von folgender Veranstaltung abgemeldet:</p>
<p>{{changed-registrations}}</p>
<p>Die Abmeldung bezieht sich nur auf die aufgef&uuml;hrte Veranstaltung. Falls uns ein Fehler unterlaufen sein sollte, bitten wir Sie, sich mit uns <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail </a>in Verbindung zu setzen.</p>
<p>Eine Auflistung Ihrer Veranstaltungsanmeldungen f&uuml;r das aktuelle Semester finden Sie unter folgendem Link: {{login-link}}</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You have been deregistered from the following event:</p>
<p>{{changed-registrations}}</p>
<p>The deregistration refers only to the listed event. If we have made a mistake, please contact us by e-mail.</p>
<p>A list of event registrations for the current semester can be found at the followig link: {{login-link}}</p>
<p>We look forward to welcoming you to one of our events in the future and recommend our lecture and workshop program at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a> for this purpose.</p>
<p>Best regards</p>
      `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.ABMELDUNG),
  attachments: [],
  displayName: 'Abmeldung',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.ABMELDUNG),
});

function assertExists<T extends any>(d: Maybe<T>, name?: string): asserts d is T {
  if (d === null)
    throw new Error(
      name ? `expected value to be defined, but it is null (${name})` : 'expected value to be defined, but it is null',
    );
  if (d === undefined)
    throw new Error(
      name
        ? `expected value to be defined, but it is undefined (${name})`
        : 'expected value to be defined, but it is undefined',
    );
}

// TODO: remove property useOldSlots and handle that case more gracefully
export async function notifyAbmeldung(data: { participationID: string; throughAdmin?: boolean; useOldSlots?: any[] }) {
  // TODO: implement whatsapp

  const { participationID } = data;
  const participationCol = await getRawCollection<Participation>(Participation._collectionName);
  const participation = await participationCol.findOne<Participation>({ _id: participationID });

  assertExists(participation, 'participation');

  const { productID, userID } = participation;
  const product = await getByID(
    Product,
    productID,
  )({
    _id: 1,
    topic: 1,
    presenceType: 1,
    institutionID: 1,
    language: 1,
    name: 1,
    slots: { _id: 1, start: 1, end: 1, berater: 1 },
    appointments: { name: 1, start: 1, end: 1, location: 1, lastReminderMailSent: 1 },
    settings: Setting.allFields,
  });
  assertExists(product, 'product');
  const userCol = await getRawCollection<User>(User._collectionName);
  const user = await userCol.findOne<User>({ _id: userID });
  if (!user) throw new Error('user not found');
  assertExists(user, 'user');

  if (data.useOldSlots) product.slots = data.useOldSlots;

  // set product for later data retrieval
  participation.product = product;

  const institutionID = product.institutionID;
  const institution = await getByID(
    Institution,
    institutionID,
  )({
    ...Institution.allFields,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  assertExists(institution, 'institution');

  const start = product.appointments?.[0]?.start;
  const daysTillEvent = start?.diff(DateTime.now()).as('days').toFixed(0);
  let dates = product.appointments?.map(a => a.start.toFormat('dd.LL.yyyy')).join(', ');
  if (!dates) dates = start?.toFormat('dd.LL.yyyy');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const templateType = data.throughAdmin ? MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG : MailTemplateType.ABMELDUNG;

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = (await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    templateType,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(templateType).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: participation.product?.name || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: product.name,
      [MailTemplateVars.EVENT_DATES]: dates ?? '',
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.CHANGED_REGISTRATIONS]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: product,
          newState: ParticipationState.LEFT,
          newWaitingListPlace: null,
          participation,
          institution,
          englishVersion: false,
        }),
      ],
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: product,
          newState: ParticipationState.LEFT,
          newWaitingListPlace: null,
          participation,
          institution,
          englishVersion: true,
        }),
      ],
      [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
        participations: [participation],
        institution,
        products: [product],
        englishVersion: false,
      }),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
